import React from 'react';

// Support both snake_case (API) and camelCase formats
interface PerformanceSummaryData {
  flowboardId?: string;
  cycleName?: string;
  overallRating?: number;
  selfAssessmentStatus?: string;
  managerReviewStatus?: string;
  calibrationStatus?: string;
  calibratedScore?: number;
  daysRemaining?: number;
  reviewDueDate?: string;
  // snake_case (from API)
  flowboard_id?: string;
  cycle_name?: string;
  overall_rating?: number;
  self_assessment_status?: string;
  manager_review_status?: string;
  calibration_status?: string;
  calibrated_score?: number;
  days_remaining?: number;
  review_due_date?: string;
}

// Helpers for snake_case/camelCase compatibility
const getCycleName = (d: PerformanceSummaryData) => d.cycleName || d.cycle_name || '';
const getOverallRating = (d: PerformanceSummaryData) => Number(d.overallRating || d.overall_rating || 0);
const getSelfAssessmentStatus = (d: PerformanceSummaryData) => d.selfAssessmentStatus || d.self_assessment_status || 'Pending';
const getManagerReviewStatus = (d: PerformanceSummaryData) => d.managerReviewStatus || d.manager_review_status || 'Pending';
const getCalibrationStatus = (d: PerformanceSummaryData) => d.calibrationStatus || d.calibration_status || 'Pending';
const getCalibratedScore = (d: PerformanceSummaryData): number | null => {
  const val = d.calibratedScore ?? d.calibrated_score;
  return val !== undefined && val !== null ? Number(val) : null;
};
const getDaysRemaining = (d: PerformanceSummaryData): number | null => {
  const val = d.daysRemaining ?? d.days_remaining;
  return val !== undefined && val !== null ? Number(val) : null;
};

interface CycleOption {
  value: string;
  label: string;
}

interface PerformanceSummaryWidgetProps {
  data?: PerformanceSummaryData | PerformanceSummaryData[];
  loading?: boolean;
  error?: string;
  cycleOptions?: CycleOption[];
  selectedCycleId?: string;
  onCycleChange?: (cycleId: string) => void;
  cycleOptionsLoading?: boolean;
}

const getStatusStyles = (status: string): { text: string; bg: string; icon: 'check' | 'progress' | 'pending' } => {
  const s = status?.toLowerCase().replace(/_/g, ' ');
  if (['completed', 'submitted', 'approved', 'calibrated'].includes(s)) {
    return { text: 'text-emerald-700', bg: 'bg-emerald-50', icon: 'check' };
  }
  if (['in progress', 'started'].includes(s)) {
    return { text: 'text-amber-700', bg: 'bg-amber-50', icon: 'progress' };
  }
  return { text: 'text-gray-500', bg: 'bg-gray-50', icon: 'pending' };
};

export const PerformanceSummaryWidget: React.FC<PerformanceSummaryWidgetProps> = ({
  data, loading, error, cycleOptions, selectedCycleId, onCycleChange, cycleOptionsLoading
}) => {
  const summaryData: PerformanceSummaryData | null = Array.isArray(data) ? data[0] : data || null;

  const renderHeader = (daysContent?: React.ReactNode) => (
    <div className="mb-1">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Current Performance Summary</h3>
        {cycleOptions && cycleOptions.length > 0 && onCycleChange && (
          <select
            value={selectedCycleId || ''}
            onChange={(e) => onCycleChange(e.target.value)}
            disabled={cycleOptionsLoading}
            className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 w-[200px] truncate"
          >
            {cycleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
      </div>
      {daysContent && (
        <div className="flex justify-end mt-5">
          {daysContent}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">{error}</div>
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">No performance data available</div>
      </div>
    );
  }

  const cycleName = getCycleName(summaryData);
  const overallRating = getOverallRating(summaryData);
  const selfStatus = getSelfAssessmentStatus(summaryData);
  const managerStatus = getManagerReviewStatus(summaryData);
  const calibrationStatus = getCalibrationStatus(summaryData);
  const calibratedScore = getCalibratedScore(summaryData);
  const daysRemaining = getDaysRemaining(summaryData);

  const isCalibrationDone = ['calibrated', 'completed'].includes(calibrationStatus?.toLowerCase() || '');
  const calibrationDisplay = isCalibrationDone && calibratedScore !== null && calibratedScore > 0
    ? `Calibrated (${calibratedScore.toFixed(1)})` : calibrationStatus;

  const statusItems = [
    { label: 'Self Assessment', value: selfStatus },
    { label: 'Manager Review', value: managerStatus },
    { label: 'Calibration', value: calibrationDisplay, rawStatus: calibrationStatus },
  ];

  // Determine days remaining badge style
  const getDaysStyle = (days: number | null) => {
    if (days === null) return null;
    if (days < 0) return { bg: 'bg-red-100', text: 'text-red-700', label: `${Math.abs(days)} days overdue` };
    if (days <= 7) return { bg: 'bg-red-100', text: 'text-red-700', label: `${days} days left` };
    if (days <= 14) return { bg: 'bg-amber-100', text: 'text-amber-700', label: `${days} days left` };
    return { bg: 'bg-emerald-100', text: 'text-emerald-700', label: `${days} days left` };
  };

  const daysStyle = getDaysStyle(daysRemaining);

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      {renderHeader(
        daysStyle ? (
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${daysStyle.bg} ${daysStyle.text}`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {daysStyle.label}
          </span>
        ) : undefined
      )}

      {/* Main Content */}
      <div className="flex-1 flex items-center gap-6">
        {/* Rating Circle - Left */}
        <div className="text-center flex-shrink-0">
          <div className="w-28 h-28 rounded-full border-4 border-indigo-500 flex items-center justify-center bg-indigo-50">
            <span className="text-4xl font-bold text-indigo-600">{overallRating.toFixed(1)}</span>
          </div>
          <div className="text-xs text-gray-500 mt-2">Overall Rating</div>
        </div>

        {/* Status Cards - Right */}
        <div className="flex-1 space-y-2.5">
          {statusItems.map((item) => {
            const styles = getStatusStyles(item.rawStatus || item.value);
            return (
              <div key={item.label} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${styles.bg}`}>
                {/* Status Icon */}
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${styles.icon === 'check' ? 'bg-emerald-500' :
                  styles.icon === 'progress' ? 'bg-amber-500' : 'bg-gray-300'
                  }`}>
                  {styles.icon === 'check' && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {styles.icon === 'progress' && (
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  )}
                  {styles.icon === 'pending' && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                {/* Label & Value */}
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm text-gray-700">{item.label}</span>
                  <span className={`text-sm font-medium ${styles.text}`}>{item.value}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PerformanceSummaryWidget;
